// SEQ-180 E16: profit % by project, sorted descending, with a top/bottom limit control.
//
// SEQ-180 legibility follow-up (15 September 2026). Three things were wrong
// here and two of them were the same bug wearing different clothes.
//
//   The card's chart body was capped at 200px by a `flex: 1` in
//   PnlDashboardCharts.css, so `Math.max(220, rows * 32)` had never once
//   decided this chart's height. "Top 10" drew ten bars into 200px, which is
//   20px per project INCLUDING its name, which is why axis labels wrapped
//   mid-word and ellipsised and why the card looked like it rendered about
//   seven bars.
//
//   The tooltip was fed the same 20-character truncation as the axis, so
//   "52 Merthyr Rd - New Farm" and "Esperance  Hope Island" could not be read
//   in full anywhere in the widget. The axis is the glance and the tooltip is
//   the answer: only one of them is allowed to shorten a name.
//
//   The empty test asked whether any PROJECTS had arrived, not whether any of
//   them had a figure. On a month with no data it drew five project names down
//   an axis with no bars, no X axis and no message.
//
// AND ONE THE AUDIT DID NOT REACH, found by rendering this card after the three
// above were fixed. The audit measured the scatters' outlier problem and marked
// this chart clear, because "Top 10" is the default and Top 10 cannot contain
// the worst project on the page. Switch it to "All projects" or "Bottom 10",
// which is one click, and the same defect is here in bar form: Tapestry -
// Chevron Island at -1360.1% takes a domain of -1500% to 500%, and the other
// seventeen bars become hairlines against it. Same clamp, same pinned marks,
// same note underneath (chartDomain.ts).
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList, ReferenceLine, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { BarChart3 } from "lucide-react";
import type { ProjectRollup } from "../../features/pnlAnalytics/rollup";
import { pnlChartCardStyle, formatPnlMetric } from "../../features/pnlAnalytics/pnlAnalyticsFormat";
import { countPlottable } from "../../features/pnlAnalytics/chartEmptiness";
import { clampedAxisDomain, outsideDomain, pinToDomain } from "../../features/pnlAnalytics/chartDomain";
import { truncateAxisLabel } from "../../features/pnlAnalytics/widgetChartLabels";
import { chartAnimation } from "../../features/pnlAnalytics/pnlMotion";
import { PnlAxisNote, PnlChartEmpty, PnlChartHead, type PinnedProject } from "./PnlChartFrame";
import { PnlProjectTooltip, type ProjectTooltipRow } from "./PnlProjectTooltip";
import "./PnlWidgetCharts.css";

type LimitMode = "top10" | "bottom10" | "all";

/**
 * The vertical room one project row needs: an 18px bar, the gap either side of
 * it, and a name set at 12px that has to sit beside it without being clipped.
 */
const ROW_HEIGHT = 34;

/** The floor, for a chart holding one or two projects. */
const MIN_CHART_HEIGHT = 240;

/**
 * How long an axis tick may be here.
 *
 * Much longer than the generic charts' 16, because this axis runs down the side
 * with a gutter to itself and Recharts wraps a category tick that does not fit,
 * so the budget is two lines rather than one. At 24 the longest names came out
 * BOTH wrapped and ellipsised, which is the worst of the two. 34 clears every
 * project name on this dashboard except the two longest, and the tooltip
 * carries the full name for those.
 */
const AXIS_LABEL_MAX = 34;

const AXIS_WIDTH = 176;

/** Room at the right for the figure printed at the end of each bar. */
const VALUE_LABEL_GUTTER = 52;

/**
 * What the tooltip says about a bar.
 *
 * Profit % is the bar itself and leads. The three under it are the figures that
 * make it: a 70% margin on $40k and a 70% margin on $4m are not the same news,
 * and the bar alone cannot tell them apart.
 */
const PROFIT_TOOLTIP_ROWS: ProjectTooltipRow[] = [
  { name: "Profit %", key: "profitPercent", unit: "percent", axis: true, colour: "var(--color-success)", pinnedBy: "pinnedX" },
  { name: "Revenue", key: "revenue", unit: "currency" },
  { name: "Cost", key: "cost", unit: "currency" },
  { name: "Profit", key: "profit", unit: "currency" },
];


/**
 * One bar's figure, printed just to the right of the bar.
 *
 * Recharts' own `position="right"` puts the label at the far end of the rect,
 * and for a bar running LEFT from zero that end is the axis, so the three
 * losing projects printed their figures on top of their own names.
 *
 * MEASURED RATHER THAN ASSUMED, because the obvious replacement was also wrong.
 * Recharts hands a horizontal bar an `x` at the ZERO line and a NEGATIVE
 * `width` when the value is below zero (logged from the running page:
 * `{x: 215.1, width: -29.1, value: -1360.1}`), so `x + width` is the far end
 * again and the label landed back inside the bar. Taking the greater of the two
 * gives the zero end for a losing project and the bar's tip for a winning one,
 * which is the same place to the reader: just past where the bar stops, reading
 * outward.
 */
interface BarValueLabelProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  value?: number | null;
}

function BarValueLabel({ x = 0, y = 0, width = 0, height = 0, value = null }: BarValueLabelProps) {
  return (
    <text
      x={Math.max(x, x + width) + 6}
      y={y + height / 2}
      dominantBaseline="middle"
      fontSize={12}
      fill="var(--text-secondary)"
    >
      {formatPnlMetric(value, "percent")}
    </text>
  );
}

interface PnlProfitByProjectBarProps {
  projects: (ProjectRollup & { projectName: string })[];
  /** SEQ-240: the "How is this worked out" trigger beside the title. */
  info?: ReactNode;
}

export function PnlProfitByProjectBar({ projects, info }: PnlProfitByProjectBarProps) {
  const navigate = useNavigate();
  const reduce = useReducedMotion() ?? false;
  const [limitMode, setLimitMode] = useState<LimitMode>("top10");

  const plotted = useMemo(
    () => projects.filter((p) => typeof p.profitPercent === "number" && Number.isFinite(p.profitPercent)),
    [projects],
  );

  const sorted = useMemo(
    () => [...plotted].sort((a, b) => (b.profitPercent ?? -Infinity) - (a.profitPercent ?? -Infinity)),
    [plotted],
  );

  const shown = useMemo(() => {
    if (limitMode === "top10") return sorted.slice(0, 10);
    if (limitMode === "bottom10") return sorted.slice(-10).reverse();
    return sorted;
  }, [sorted, limitMode]);

  // The domain is computed from what is ON SCREEN, not from every project, so
  // switching to Top 10 re-scales to the ten bars being compared instead of
  // leaving room for projects that are not drawn.
  const axis = useMemo(() => clampedAxisDomain(shown.map((p) => p.profitPercent)), [shown]);

  const chartData = useMemo(
    () =>
      shown.map((p) => ({
        ...p,
        // The AXIS's copy of the name. `projectName` is untouched beside it,
        // and it is the one the tooltip reads.
        displayName: truncateAxisLabel(p.projectName, AXIS_LABEL_MAX),
        // The length the bar is DRAWN to. `profitPercent` beside it is the real
        // figure, and it is what the label, the tooltip and the note print.
        plotPercent: pinToDomain(p.profitPercent as number, axis.domain),
        pinnedX: outsideDomain(p.profitPercent, axis.domain) !== null,
      })),
    [shown, axis.domain],
  );

  const pinnedProjects = useMemo<PinnedProject[]>(
    () =>
      chartData
        .filter((entry) => entry.pinnedX)
        .map((entry) => ({
          projectId: entry.projectId,
          projectName: entry.projectName,
          reading: `${formatPnlMetric(entry.profitPercent, "percent")} profit`,
        })),
    [chartData],
  );

  // Mouse-only by design: recharts bars have no keyboard focus target of
  // their own. The project performance table's project name link
  // (PnlProjectPerformanceTable.tsx) is the keyboard-accessible path to the
  // same destination for every project shown here, so this is not a
  // keyboard-access gap to fix on the chart itself (round 2 code review).
  const handleBarClick = (_data: unknown, index: number) => {
    const entry = chartData[index];
    if (entry?.projectId) navigate(`/projects/${entry.projectId}?tab=status`);
  };

  const chartHeight = Math.max(MIN_CHART_HEIGHT, chartData.length * ROW_HEIGHT);

  return (
    <div style={pnlChartCardStyle} className="pnl-chart-card">
      <PnlChartHead
        icon={<BarChart3 size={18} aria-hidden="true" style={{ color: "var(--color-primary)" }} />}
        title="Profit % by project"
        info={info}
        caption="One bar per project: its profit as a share of its own revenue, over the dates you pick. Select a bar to open that project."
      >
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, color: "var(--text-muted)" }}>
          Show
          <select
            className="form-input"
            value={limitMode}
            onChange={(e) => setLimitMode(e.target.value as LimitMode)}
            style={{ padding: "4px 8px", fontSize: 12, width: "auto" }}
          >
            <option value="top10">Top 10</option>
            <option value="bottom10">Bottom 10</option>
            <option value="all">All projects</option>
          </select>
        </label>
      </PnlChartHead>

      {countPlottable(projects, ["profitPercent"]) === 0 ? (
        <PnlChartEmpty />
      ) : (
        <motion.div
          className="pnl-chart-body"
          style={{ width: "100%", height: chartHeight }}
          // Keyed on the limit so switching between Top 10 and All re-plays the
          // draw rather than morphing ten bars into twenty-five mid-flight.
          key={limitMode}
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: reduce ? 0 : 0.2 }}
        >
          <ResponsiveContainer>
            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: VALUE_LABEL_GUTTER, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border-color)" />
              <XAxis
                type="number"
                domain={axis.domain}
                allowDataOverflow
                tickFormatter={(v: number) => `${v.toFixed(0)}%`}
                tick={{ fontSize: 12, fill: "var(--text-secondary)" }}
                axisLine={false}
              />
              <YAxis
                type="category"
                dataKey="displayName"
                width={AXIS_WIDTH}
                tick={{ fontSize: 12, fill: "var(--text-secondary)" }}
                axisLine={false}
                // Every project gets its tick. Recharts drops labels to fit
                // when it is left to choose, which is how a chart showing ten
                // bars ends up showing seven names.
                interval={0}
              />
              <Tooltip
                content={(<PnlProjectTooltip rows={PROFIT_TOOLTIP_ROWS} />) as never}
                cursor={{ fill: "rgba(0,0,0,0.04)" }}
              />
              {/* Break-even. A profit chart that crosses zero needs the line it
                  crosses, and on this data three projects cross it. */}
              <ReferenceLine x={0} stroke="var(--text-secondary)" strokeWidth={1} />
              <Bar
                dataKey="plotPercent"
                name="Profit %"
                radius={[0, 6, 6, 0]}
                barSize={18}
                cursor="pointer"
                onClick={handleBarClick}
                activeBar={{ fillOpacity: 0.82 }}
                {...chartAnimation(reduce, 0)}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`profit-pct-${index}`}
                    fill={
                      entry.pinnedX
                        ? "var(--color-error-text)"
                        : (entry.profitPercent ?? 0) >= 0
                          ? "var(--color-success)"
                          : "var(--color-error)"
                    }
                  />
                ))}
                {/* The figure at the end of its own bar. Ten rows at 34px each
                    is enough room to print it, and a reader who can see the
                    number does not have to hover ten times to rank two projects
                    that look alike. The value printed is always the REAL one,
                    including for a bar whose length was clamped. */}
                <LabelList dataKey="profitPercent" content={BarValueLabel as never} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      )}

      <PnlAxisNote beyond="the profit axis" projects={pinnedProjects} onOpenProject={(id) => navigate(`/projects/${id}?tab=status`)} />
    </div>
  );
}
